import Layout from "../../Layout/Layout.component";
import { AppContext } from "../../Context/MainContext";
import { useContext, useEffect, useState } from "react";
import { calculateControlSheetData } from "../../utils/controlSheet.utils";
import DataTable from "react-data-table-component";
import { formatCurrency } from "../../utils/number.utils";
import InfoCard from "../../Components/InfoCard.component";
import SelectFilter from "../../Components/Filtering/SelectFilter.component";

const ControlSheetDashboardPage = () => {

    const { transactions, controlSheets, selectedProject } = useContext(AppContext);

    const [data, setData] = useState<any[]>([]);
    const [resume, setResume] = useState<any>({});
    const [filterFamily, setFilterFamily] = useState('');

    useEffect(() => {
        if (controlSheets.length > 0) {
            let datum: any[] = [];

            controlSheets.forEach((cs: any) => {
                let d: any = calculateControlSheetData(cs, transactions);

                d.invoices = d.invoices.filter((t: any) => {
                    const statusId = t.status?._id || t.status;
                    return statusId === 'Ingresada' || statusId === 'Aprobada';
                });

                d.invoicesTotal = d.invoices.reduce((acc: number, curr: any) => acc + (curr.total || 0), 0) - d.NNCCsTotal;
                d.paidInvoices = d.invoices.filter((t: any) => t.paymentStatus && t.paymentStatus.toLowerCase().includes("pagada")).reduce((acc: number, curr: any) => acc + (curr.total || 0), 0) - d.NNCCsTotal;
                d.pendingInvoices = d.invoicesTotal - d.paidInvoices;

                if (cs.codes && cs.codes.length > 0) {
                    cs.codes.forEach((code: any) => {
                        const familyName = code.family;

                        if (filterFamily) {
                            if (filterFamily === "Sin Familia" && familyName) return;
                            if (filterFamily !== "Sin Familia" && (!familyName || !familyName.toLowerCase().includes(filterFamily.toLowerCase()))) return;
                        }

                        const budget = code.budget || cs.budget || 0;

                        // Filter transactions, agreements, observations by family
                        const familyAgreements = d.agreements.filter((a: any) => a.family === familyName);
                        const agreementsTotal = familyAgreements.reduce((acc: number, curr: any) => acc + (curr.amount || 0), 0);

                        const familyObservations = d.observations.filter((o: any) => o.family === familyName);
                        const recoverable = familyObservations.reduce((acc: number, curr: any) => acc + (curr.recoverAmount || 0), 0);

                        const total = budget + recoverable;

                        // Transactions already partitioned in calculateControlSheetData but need family filtering here
                        const familyInvoices = d.invoices.filter((t: any) => t.family === familyName);
                        const familyNNCCs = d.NNCCs.filter((t: any) => t.family === familyName);
                        const familyOCs = d.OCs.filter((t: any) => t.family === familyName);
                        const familyPaidInvoices = familyInvoices.filter((t: any) => t.paymentStatus && t.paymentStatus.toLowerCase().includes("pagada"));

                        const nnccsSum = familyNNCCs.reduce((acc: number, curr: any) => acc + (curr.total || 0), 0);
                        const invoicesTotal = familyInvoices.reduce((acc: number, curr: any) => acc + (curr.total || 0), 0) - nnccsSum;
                        const paidInvoices = familyPaidInvoices.reduce((acc: number, curr: any) => acc + (curr.total || 0), 0) - nnccsSum;

                        datum.push({
                            rowName: `${cs.name} - ${familyName}`,
                            budget,
                            agreementsTotal,
                            recoverable,
                            total,
                            balance: agreementsTotal - invoicesTotal,
                            invoicesTotal,
                            paidInvoices,
                            pendingInvoices: invoicesTotal - paidInvoices
                        });
                    });
                } else {
                    if (filterFamily && filterFamily !== "Sin Familia") return;

                    d.rowName = `${cs.name} - Sin familia`;
                    d.balance = d.agreementsTotal - d.invoicesTotal;
                    datum.push(d);
                }
            });

            let total: any = {};
            datum.forEach((d: any) => {
                total["Monto propuesta"] = total["Monto propuesta"] ? total["Monto propuesta"] + d.budget : d.budget;
                total["Acuerdos"] = total["Acuerdos"] ? total["Acuerdos"] + d.agreementsTotal : d.agreementsTotal;
                total["Recuperable"] = total["Recuperable"] ? total["Recuperable"] + d.recoverable : d.recoverable;
                total["Total + Extras"] = total["Total + Extras"] ? total["Total + Extras"] + d.total : d.total;
                total["Balance"] = total["Balance"] ? total["Balance"] + d.balance : d.balance;
                total["Facturas"] = total["Facturas"] ? total["Facturas"] + d.invoicesTotal : d.invoicesTotal;
                total["Facturas Pagadas"] = total["Facturas Pagadas"] ? total["Facturas Pagadas"] + d.paidInvoices : d.paidInvoices;
                total["Facturas Pendientes"] = total["Facturas Pendientes"] ? total["Facturas Pendientes"] + d.pendingInvoices : d.pendingInvoices;
            });

            datum.sort((a, b) => a.rowName.localeCompare(b.rowName));

            setData(datum);
            setResume(total);
        }

    }, [transactions, controlSheets, filterFamily]);

    console.log(resume);

    let cols = [
        { name: <div>Hojas de Control - Familia</div>, wrap: true, selector: (row: any) => row.rowName, sortable: true, width: "300px" },
        { name: <div>Monto Propuesta</div>, wrap: true, selector: (row: any) => `$${formatCurrency(row.budget)}`, sortable: true },
        { name: <div>Acuerdos</div>, wrap: true, selector: (row: any) => `$${formatCurrency(row.agreementsTotal)}`, sortable: true },
        { name: <div>Recuperable</div>, wrap: true, selector: (row: any) => `$${formatCurrency(row.recoverable)}`, sortable: true },
        { name: <div>Total + Extras</div>, wrap: true, selector: (row: any) => `$${formatCurrency(row.total)}`, sortable: true },
        { name: <div>Balance</div>, wrap: true, selector: (row: any) => row.balance, cell: (row: any) => <span className={row.balance < 0 ? "text-red-500 font-bold" : ""}>{row.balance < 0 ? `-$${formatCurrency(Math.abs(row.balance))}` : `$${formatCurrency(row.balance)}`}</span>, sortable: true },
        { name: <div>Facturas</div>, wrap: true, selector: (row: any) => row.invoicesTotal, cell: (row: any) => <span className={row.invoicesTotal < 0 ? "text-red-500 font-bold" : ""}>{row.invoicesTotal < 0 ? `-$${formatCurrency(Math.abs(row.invoicesTotal))}` : `$${formatCurrency(row.invoicesTotal)}`}</span>, sortable: true },
        { name: <div>Facturas Pagadas</div>, wrap: true, selector: (row: any) => row.paidInvoices, cell: (row: any) => <span className={row.paidInvoices < 0 ? "text-red-500 font-bold" : ""}>{row.paidInvoices < 0 ? `-$${formatCurrency(Math.abs(row.paidInvoices))}` : `$${formatCurrency(row.paidInvoices)}`}</span>, sortable: true },
        { name: <div>Facturas Pendientes</div>, wrap: true, selector: (row: any) => row.pendingInvoices, cell: (row: any) => <span className={row.pendingInvoices < 0 ? "text-red-500 font-bold" : ""}>{row.pendingInvoices < 0 ? `-$${formatCurrency(Math.abs(row.pendingInvoices))}` : `$${formatCurrency(row.pendingInvoices)}`}</span>, sortable: true },
    ];

    return (
        <Layout>
            <div className="container">
                <h1 className="text-2xl font-bold mb-1">Dashboard Control de Hojas</h1>
                <p className="text-gray-500 text-sm mb-4">* Los montos de Facturas y Facturas Pagadas ya incluyen el descuento de las Notas de Crédito (NNCC).</p>
                {selectedProject && selectedProject.families && (
                    <div className="flex gap-4 mb-4">
                        <SelectFilter
                            label="Familia"
                            selectOptions={["Sin Familia", ...selectedProject.families.filter((f: any) => f.selectable).sort((a: any, b: any) => a.name.localeCompare(b.name)).map((f: any) => f.name)]}
                            selected={filterFamily}
                            onFilter={value => setFilterFamily(value)}
                            onClear={() => setFilterFamily('')}
                        />
                    </div>
                )}
            </div>
            <div className="flex gap-2 my-4">
                {Object.keys(resume).map((key: string) => {
                    return <InfoCard key={key} title={key} content={[
                        `$${formatCurrency(resume[key])}`
                    ]}
                    />
                })}

            </div>
            <DataTable
                columns={cols}
                data={data}
            />
        </Layout>
    );
}

export default ControlSheetDashboardPage;